"use client";

import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import { AlertCircle, Loader2, Save, Trash2, X } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";

import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
} from "../../../redux/slices/fileSlice.js";
import { OfflineImage } from "../../shared/offline-image";
import { Alert, AlertDescription } from "../../ui/alert.jsx";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import { LoadingIndicator } from "../../ui/loading-indicator";
import {
  OptimizedCheckboxGroup,
  OptimizedTextarea,
} from "../optimized-form-field";

import { useNotification } from "../../../hooks/useNotification";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { sharedLinesPipesDrainsProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";

import { LocalSectionImportControl } from "./local-section-import-control";

interface LinesPipesDrainsFormProps extends AuditFormProps {
  siteId: string;
  templateId: string;
  readOnly?: boolean;
  reportId?: string;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  previousCompletedReports?: any[];
}

interface InfrastructureImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface LinesPipesDrainsData {
  // Infrastructure Types
  infrastructureTypes: string[];

  // Additional Information
  additionalDetails: string;
  locationDescription: string;
  depthInformation: string;
  safetyConsiderations: string;
  accessPoints: string;

  // Images
  infrastructureImages: InfrastructureImage[];

  // Notes
  generalNotes: string;
  surveyNotes: string;
  maintenanceNotes: string;
}

const initialData: LinesPipesDrainsData = {
  infrastructureTypes: [],
  additionalDetails: "",
  locationDescription: "",
  depthInformation: "",
  safetyConsiderations: "",
  accessPoints: "",
  infrastructureImages: [],
  generalNotes: "",
  surveyNotes: "",
  maintenanceNotes: "",
};

const infrastructureOptions = [
  {
    value: "electrical-power-lines",
    label: "Electrical power lines across land",
  },
  {
    value: "underground-electrical-lines",
    label: "Underground electrical lines",
  },
  { value: "water-pipes", label: "Water pipes" },
  { value: "underground-water-supply", label: "Underground water supply" },
  { value: "gas-lines", label: "Gas lines" },
  { value: "telephone-lines", label: "Telephone lines" },
  { value: "drainage", label: "Drainage" },
  { value: "underground-foundations", label: "Underground foundations" },
  { value: "no-subsurface-lines", label: "No subsurface lines" },
  {
    value: "overhead-power-lines",
    label: "Overhead power lines nearby",
  },
  {
    value: "hvac-equipment",
    label: "HVAC equipment on roof",
  },
  {
    value: "roof-vents-skylights",
    label: "Roof vents / skylights present",
  },
  {
    value: "existing-cable-trays",
    label: "Existing cable trays or conduits",
  },
  {
    value: "lightning-protection-system",
    label: "Lightning protection system",
  },
  {
    value: "communication-antennas",
    label: "Communication antennas or telecom equipment",
  },
].sort((a, b) => a.label.localeCompare(b.label));

export const LinesPipesDrainsForm = memo(function LinesPipesDrainsForm({
  siteId,
  templateId,
  reportId,
  sessionProgress,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: LinesPipesDrainsFormProps) {
  const { notify } = useNotification();

  const { loading } = useAppSelector((state) => state?.files);
  const [formData, setFormData] = useState<LinesPipesDrainsData>(initialData);
  const formDataRef = useLatest(formData);
  const [selectedImage, setSelectedImage] =
    useState<InfrastructureImage | null>(null);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions] = useImageDescriptions(files);

  // Load version data when viewing historical version
  const currentSiteData = useVersionAwareSiteData(readOnly);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power). Mirrors the pattern in site-overview.tsx / site-data.tsx.
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:lines-pipes-drains` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  // Restore any unsaved draft once on mount, before server data can
  // overwrite it below. Only applies if a draft exists, so a freshly-fetched
  // server record from a different session is never clobbered unexpectedly.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once the local form state matches what's confirmed saved on the server,
  // the IndexedDB draft is redundant — clear it so a future mount doesn't
  // restore stale local data over a fresher server record.
  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    const prog = sharedLinesPipesDrainsProgress(formData);
    setSessionProgress((prev) => ({
      ...prev,
      [templateId]: { ...prev[templateId], "lines-pipes-drain": prog },
    }));
  }, [formData, setSessionProgress, templateId]);

  // Fetch existing files
  useEffect(() => {
    dispatch(
      fetchFiles({
        siteId,
        reportId,
      }),
    );
  }, [dispatch, siteId, reportId]);

  // Save handler
  const handleSave = useCallback(
    async (data: LinesPipesDrainsData) => {
      try {
        const mutationResult1 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: { infrastructureData: data },
            }),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Lines, Pipes & Drains",
          [mutationResult1],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        console.error("[v0] Save failed:", error);
        const config = notificationHelpers.formSaveError(
          "Lines, Pipes & Drains",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [siteId, dispatch, notify],
  );

  // Debounced save
  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 7500,
    onSave: handleSave,
    onError: (error) => console.error("Save error:", error),
    readOnly,
  });

  useEffect(() => {
    if (currentSiteData) {
      const newFormData = {
        ...formDataRef.current,
        ...(currentSiteData?.infrastructureData || {}),
      };
      resetFormBaseline(newFormData);
      setFormData(newFormData);
    }
  }, [currentSiteData, resetFormBaseline, formDataRef]);

  // Save handler for image descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult2 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult2],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for image descriptions (5000ms delay)
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving = isFormSaving || isImageSaving;
  const hasUnsavedChanges = hasFormUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    if (!lastSavedForm) return lastSavedImage;
    if (!lastSavedImage) return lastSavedForm;
    return lastSavedForm > lastSavedImage ? lastSavedForm : lastSavedImage;
  }, [lastSavedForm, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
  }, [forceFormSave, forceImageSave]);

  // Form validation - at minimum, infrastructure types should be selected
  const isFormValid = useMemo(() => {
    return formData?.infrastructureTypes?.length > 0;
  }, [formData]);

  // Handle infrastructure type selection with special logic for "no-subsurface-lines"
  const handleInfrastructureTypeChange = useCallback(
    (selectedTypes: string[]) => {
      setFormData((prev) => {
        let newTypes = [...selectedTypes];

        // If "no-subsurface-lines" is selected, clear all other selections
        if (selectedTypes.includes("no-subsurface-lines")) {
          newTypes = ["no-subsurface-lines"];
        }
        // If other types are selected and "no-subsurface-lines" was previously selected, remove it
        else if (
          prev.infrastructureTypes.includes("no-subsurface-lines") &&
          selectedTypes.length > 0
        ) {
          newTypes = selectedTypes.filter(
            (type) => type !== "no-subsurface-lines",
          );
        }

        return { ...prev, infrastructureTypes: newTypes };
      });
    },
    [],
  );

  // Field change handler
  const handleFieldChange = useCallback((field: keyof LinesPipesDrainsData) => {
    return (value: string | string[] | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };
  }, []);

  // Image upload handler

  // Update image description
  // Update image description — updates local state; debounced save handles the dispatch

  // Remove image handlers

  const confirmRemoveImage = useCallback(() => {
    if (imageToDelete) {
      requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "Image",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "Image",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setImageToDelete(null);
        });
    }
  }, [imageToDelete, dispatch, notify]);

  const cancelRemoveImage = useCallback(() => {
    setImageToDelete(null);
  }, []);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // Get loading status
  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  // Check if uploading for a section

  // Image Card Component

  // Image Section Component

  return (
    <div className="space-y-4 md:space-y-6 relative">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="lines-pipes-drain"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          setFormData={setFormData}
        />
      )}

      {/* Floating save section - hidden when viewing read-only version */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Infrastructure Types Card */}
      <Card>
        <CardHeader>
          <CardTitle>Lines / Wires / Pipes / Drains</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-slate-600 dark:text-muted-foreground">
              Select all infrastructure types present on the site. If no
              subsurface lines are present, select only "No subsurface lines".
            </p>
          </div>

          <OptimizedCheckboxGroup
            id="infrastructureTypes"
            label="Infrastructure Types"
            values={formData.infrastructureTypes}
            onChange={handleInfrastructureTypeChange}
            readOnly={readOnly}
            options={infrastructureOptions}
            required
            direction="column"
          />

          {formData.infrastructureTypes.includes("no-subsurface-lines") && (
            <Alert className="bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700">
                "No subsurface lines" selected. This will clear any other
                infrastructure selections.
              </AlertDescription>
            </Alert>
          )}

          <OptimizedTextarea
            id="additionalDetails"
            label="Additional Details"
            value={formData.additionalDetails}
            onChange={handleFieldChange("additionalDetails")}
            placeholder="Provide additional details about the infrastructure..."
            rows={3}
            readOnly={readOnly}
          />
        </CardContent>
      </Card>

      {/* Form validation alert */}
      {!isFormValid && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select at least one infrastructure type to complete this
            section.
          </AlertDescription>
        </Alert>
      )}

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Delete Image</h3>
            </div>
            <p className="text-slate-600 dark:text-muted-foreground mb-6">
              Are you sure you want to delete this image? This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={cancelRemoveImage}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRemoveImage}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
                disabled={loading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={
                  selectedImage?._id ||
                  selectedImage?.id ||
                  selectedImage?._clientId
                }
                src={selectedImage?.fileUrl}
                alt={selectedImage.originalName}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                {new Date(selectedImage.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress footer */}
      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Form Status:
            </span>
            <Badge variant={isFormValid ? "default" : "secondary"}>
              {isFormValid ? "Complete" : "Incomplete"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Progress:
            </span>
            <Badge variant="outline">
              {sessionProgress?.[templateId]?.["lines-pipes-drain"] ||
                sharedLinesPipesDrainsProgress(formData)}
              %
            </Badge>
          </div>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
